import {
  Card,
  Profile,
  ReviewLog,
  UserKnownWord,
  isLocked,
  isTranslationLanguage,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import {
  SETTING_RANGES,
  SyncOp,
  SyncOpBody,
  isDevMockUserId,
  newUuid,
  opCardId,
  syncKeys,
} from '../storage/outbox.js';
import { platform } from '../platform.js';
import { rowToCard, rowToKnownWord, rowToReviewLog } from './cardRows.js';
import { getAccess, saveAccess } from './access.js';
import { moveOverToCloudFirst } from './legacyMerge.js';
import {
  SyncAnswer,
  SyncRequestError,
  SyncTransport,
  edgeFunctionTransport,
  syncClient,
} from './syncTransport.js';
import {
  clearUpdateRequired,
  rememberMinExtensionVersion,
  setUpdateRequired,
} from './forceUpdate.js';

/**
 * The device's side of the sync (Docs/SYNC_CONTRACT.md, section 5).
 *
 * The server's copy of a learner's cards, known words, review history and settings is the real
 * one. This device keeps a copy of it and an outbox of the learner's changes the server has not
 * answered yet (both in storage/index.ts). The engine does the talking:
 *
 *   - it sends the outbox in order, at most 200 operations per request, about a second after
 *     the last change;
 *   - with the same request it asks what changed since the last answer (the "cursor") and
 *     writes the rows it gets into the saved copy;
 *   - when a request fails for a reason that may pass (no connection, a server error, no answer
 *     in time) nothing is dropped: the same operations are sent again later, first after a few
 *     seconds, then less and less often, at most every five minutes.
 *
 * SEVERAL PAGES AT ONCE. The dashboard, the popup, the review window and the background worker
 * each run their own engine on the same storage. Two rules keep them out of each other's way:
 *
 *   1. Only one of them talks to the server at a time: a whole round (send, receive, write) is
 *      done under the `sync` lock (a Web Lock, see StorageManager.locked). The others wait and
 *      then start from what is in storage by then. This matters: every operation is safe to send
 *      twice, but two requests carrying the same operations at the same moment could apply "retire"
 *      and its undo in the wrong order.
 *   2. Nobody keeps the outbox in memory. Every read-change-write of the outbox and of the
 *      saved copy happens under the short `data` lock, and an answered operation is removed by
 *      its id from the outbox as it is in storage at that moment. So an operation another page
 *      added while a request was on its way is never dropped: it was not in the request, it is
 *      not answered, it stays.
 *
 * A page closed (or the worker stopped) in the middle of a request leaves the outbox as it was:
 * the browser releases the lock, and whoever syncs next sends the same operations again. An
 * answer is written in this order: the saved copy first, then the engine's notes with the new
 * cursor, the outbox last. Stopping between any two steps only means that some rows or
 * operations travel once more, which changes nothing.
 */

/** Operations per request (the server takes at most 200). */
const MAX_OPS_PER_REQUEST = 200;
/** How long after the last change the outbox is sent. */
const SEND_DELAY_MS = 1000;
/** How long a request may take before it counts as failed (the lock must not be held for ever). */
const REQUEST_TIMEOUT_MS = 30_000;
/** The first retry after a failure; doubles each time up to the maximum. */
const RETRY_FIRST_MS = 2000;
const RETRY_MAX_MS = 5 * 60 * 1000;
/** How often an open page asks the server what changed. */
const PAGE_PULL_INTERVAL_MS = 60_000;

export type SyncOutcome =
  /** The outbox is empty and the saved copy is what the server has. */
  | 'synced'
  /** No connection or a server error; the engine tries again by itself. */
  | 'failed'
  /** Nobody is signed in with the server, or the account is the simulated development one. */
  | 'off'
  /** The time limit of `syncNow` ran out; the sync goes on in the background. */
  | 'timeout';

export interface SyncEngineOptions {
  /** How to reach the server. Default: the `sync` Edge Function with the signed-in session. */
  transport?: SyncTransport;
  /** The one-time move of an old installation. Default: services/legacyMerge.ts. */
  moveOver?: (userId: string) => Promise<void>;
  sendDelayMs?: number;
  requestTimeoutMs?: number;
  /** For tests: the spread of retry delays (0..1), instead of Math.random. */
  random?: () => number;
}

export interface SyncEngine {
  /**
   * Adds an operation to the outbox without changing the saved copy (StorageManager's methods
   * do both, and are what the app uses).
   */
  enqueue(op: SyncOpBody): Promise<void>;
  /**
   * Sends the outbox and fetches what changed, now. With `timeoutMs` it answers 'timeout' after
   * that long and lets the sync finish in the background. Never throws.
   */
  syncNow(options?: { timeoutMs?: number }): Promise<SyncOutcome>;
  /** `syncNow` unless the engine is waiting out a failure: for the timer of an open page. */
  tick(): Promise<SyncOutcome>;
  /** How many of the signed-in learner's changes the server has not confirmed yet. */
  pendingCount(): Promise<number>;
  /** After a failure: the delay before the engine tries again by itself (0 when all is well). */
  retryDelayMs(): number;
  /** Called after the saved copy changed because of an answer (any page's, on this device). */
  onCacheChanged(listener: () => void): () => void;
  /** Called after the account's settings arrived changed (the reminder is rescheduled then). */
  onSettingsChanged(listener: (profile: Profile) => void): () => void;
  /** Called after operations were sent and answered. */
  onSent(listener: () => void): () => void;
  /** Called when the server refused this extension as too old (a forced update). */
  onUpdateRequired(listener: () => void): () => void;
  /** Stops timers and listening. Operations stay in the outbox. */
  stop(): void;
}

/**
 * What the engine remembers between requests, per learner, in storage (so a page opened later
 * or the restarted worker carries on from it).
 */
interface SyncNotes {
  /** Where the next pull starts; null = everything (a full refresh). Opaque: made by the server. */
  cursor: string | null;
  /** The last answer said there is more: the next request carries the cursor and no operations. */
  paging: boolean;
  /**
   * Cards and known words whose operations were answered during a pull that still has answers
   * to come. The rows those operations changed arrive with the LAST answer, so until then an
   * older row must not replace what the learner sees.
   */
  settlingCards: string[];
  settlingKnown: string[];
  /**
   * Rows that arrived for a card while operations for it were still waiting. They are not
   * written (the learner's own change must not flicker back), but kept: when the waiting
   * operations turn out to change nothing on the server, no newer row follows, and this one is
   * the card as the server has it.
   */
  held: Record<string, Card>;
  /** During a full refresh: the ids received so far. What is not among them at the end goes. */
  refresh: { cards: string[]; known: string[]; logs: string[] } | null;
  /** Counts answers that changed the saved copy; lets other pages notice and redraw. */
  rev: number;
  /**
   * The account's `data_reset_at` as last seen (undefined: not seen yet). When the server shows
   * another one, the learner's data was wiped ("start over as a new user", on staging): the
   * saved copy is thrown away and fetched again, review history included.
   */
  reset?: string | null;
}

const EMPTY_NOTES: SyncNotes = {
  cursor: null,
  paging: false,
  settlingCards: [],
  settlingKnown: [],
  held: {},
  refresh: null,
  rev: 0,
};

async function readNotes(userId: string): Promise<SyncNotes> {
  const stored = await StorageManager.getLocalValue<Partial<SyncNotes>>(syncKeys(userId).state);
  return { ...EMPTY_NOTES, held: {}, ...stored };
}

/** The id this installation sends with every request, made once per learner and device. */
async function deviceId(userId: string): Promise<string> {
  const key = syncKeys(userId).deviceId;
  return StorageManager.locked('data', async () => {
    const stored = await StorageManager.getLocalValue<string>(key);
    if (stored) return stored;
    const id = newUuid();
    await StorageManager.setLocalValue(key, id);
    return id;
  });
}

const wordKey = (language: string, lemma: string) => `${language}\n${lemma.toLowerCase()}`;

/**
 * One of the saved lists while an answer is written into it: rows by id, new rows in front
 * (the lists are kept newest first), the order otherwise untouched.
 */
class Rows<T extends { id: string }> {
  private readonly fresh = new Map<string, T>();
  private readonly rest: Map<string, T>;
  changed = false;

  constructor(rows: T[]) {
    this.rest = new Map(rows.map((row) => [row.id, row]));
  }

  get(id: string): T | undefined {
    return this.rest.get(id) ?? this.fresh.get(id);
  }

  put(row: T): void {
    // The server sends recent rows again to be safe; the same row twice is no change.
    const before = this.get(row.id);
    if (before && JSON.stringify(before) === JSON.stringify(row)) return;
    (this.rest.has(row.id) ? this.rest : this.fresh).set(row.id, row);
    this.changed = true;
  }

  drop(id: string): void {
    if (this.rest.delete(id) || this.fresh.delete(id)) this.changed = true;
  }

  keepOnly(keep: (row: T) => boolean): void {
    for (const row of this.list()) if (!keep(row)) this.drop(row.id);
  }

  list(): T[] {
    return [...[...this.fresh.values()].reverse(), ...this.rest.values()];
  }
}

/** What an answer did, for the engine to act on after the storage lock is released. */
interface Applied {
  /** False when somebody else is signed in by now: the answer was thrown away. */
  current: boolean;
  /** Ask again at once: the answer was one of several, or a full refresh is needed. */
  again: boolean;
  outboxLeft: number;
  /** The profile as saved now, when the account's settings arrived changed. */
  settingsChanged: Profile | null;
}

/**
 * Writes one answer into storage: removes the answered operations, applies the rows (rows
 * first, then deletions), and notes the new cursor. Everything under the `data` lock, reading
 * the outbox and the saved copy afresh.
 */
async function applyAnswer(userId: string, sent: SyncOp[], answer: SyncAnswer): Promise<Applied> {
  const results = new Map(answer.results.map((r) => [r.id, r]));
  if (sent.some((op) => !results.has(op.id))) {
    throw new SyncRequestError('retry', 'The answer does not cover every operation that was sent.');
  }

  return StorageManager.locked('data', async (): Promise<Applied> => {
    if ((await StorageManager.getCurrentUserId({ fresh: true })) !== userId) {
      return { current: false, again: false, outboxLeft: 0, settingsChanged: null };
    }
    const keys = syncKeys(userId);
    const notes = await readNotes(userId);
    const sentIds = new Set(sent.map((op) => op.id));
    // An operation answered `locked` (no access) is not final: it stays, in its place, until
    // the server takes it once access is back.
    const lockedIds = new Set(
      answer.results.flatMap((r) => (!r.ok && r.error === 'locked' ? [r.id] : []))
    );
    let left = (await StorageManager.getOutbox(userId)).filter(
      (op) => !sentIds.has(op.id) || lockedIds.has(op.id)
    );

    const cards = new Rows<Card>(await StorageManager.getCards());
    const known = new Rows<UserKnownWord>(await StorageManager.getKnownWords());
    const logs = new Rows<ReviewLog>(await StorageManager.getReviewLogs());
    const held = { ...notes.held };
    const dropCard = (id: string) => {
      delete held[id];
      cards.drop(id);
    };

    // 1. What the server said to each operation. An operation answered with an error is final:
    //    it is never sent again.
    let refreshNeeded = false;
    // A card added here that the learner turned out to have already (added on another device):
    // the id of this device's card, and the card that counts.
    const sameCard = new Map<string, string>();
    /** What the learner did with a card that goes on applying to the card that counts. */
    const moved = (op: SyncOp): SyncOp | null =>
      (op.type === 'card.review' || op.type === 'card.note' || op.type === 'card.retire') &&
      sameCard.has(op.card_id)
        ? { ...op, card_id: sameCard.get(op.card_id)! }
        : null;
    const sendAgain: SyncOp[] = [];
    for (const op of sent) {
      const result = results.get(op.id)!;
      if (!result.ok && result.error === 'locked') continue;
      const cardId = opCardId(op);
      if (result.ok) {
        if (op.type === 'card.delete') dropCard(op.card_id);
        if (op.type === 'known.remove') known.drop(op.word_id);
        continue;
      }
      if (op.type === 'card.add' && result.error === 'duplicate' && result.duplicate_of) {
        sameCard.set(op.card.id, result.duplicate_of);
      }
      // Sent in the same request as the card it was made on, which was not taken: the server
      // knew no such card. It is the learner's review (note, retirement) of that word all the
      // same, so it is sent for the card that counts. Nothing was applied, and it keeps its id.
      const redirected = result.error === 'not_found' ? moved(op) : null;
      if (redirected) {
        sendAgain.push(redirected);
        continue;
      }
      console.warn(`[LingoPing Sync] The server refused "${op.type}" (${result.error}).`, op.id);
      // The review was not stored, so it is not part of the history.
      if (op.type === 'card.review') logs.drop(op.id);
      if (cardId && ['gone', 'not_found', 'duplicate'].includes(result.error)) {
        // The card is not the learner's (any more). For `duplicate` their card for the word is
        // `duplicate_of`, which arrives with the rows below.
        dropCard(cardId);
      } else {
        // The saved copy holds a change the server did not take: fetch everything again.
        refreshNeeded = true;
      }
    }

    if (sameCard.size > 0) {
      left = [...sendAgain, ...left.map((op) => moved(op) ?? op)];
      for (const log of logs.list()) {
        const card = sameCard.get(log.card_id);
        if (card) logs.put({ ...log, card_id: card });
      }
    }

    // 2. What is still waiting to be sent, or was answered but has not settled.
    const cardIds = (ops: SyncOp[]) => ops.map(opCardId).filter((id): id is string => !!id);
    const addedWords = (ops: SyncOp[]) =>
      ops.flatMap((op) => (op.type === 'known.add' ? op.words.map((w) => w.id) : []));
    const waitingCards = new Set(cardIds(left));
    const settlingCards = new Set(answer.more ? [...notes.settlingCards, ...cardIds(sent)] : []);
    const untouchable = (id: string) => waitingCards.has(id) || settlingCards.has(id);
    const waitingKnownAdds = new Set(addedWords(left));
    const answeredKnownAdds = new Set([...notes.settlingKnown, ...addedWords(sent)]);
    const waitingKnownRemovals = new Set(
      left.flatMap((op) => (op.type === 'known.remove' ? [op.word_id] : []))
    );
    const waitingGroupRemovals = new Set(
      left.flatMap((op) =>
        op.type === 'known.remove_group' ? [wordKey(op.language, op.base_lemma)] : []
      )
    );
    const waitingReviews = new Set(
      left.flatMap((op) => (op.type === 'card.review' ? [op.id] : []))
    );
    // Known words this device made itself and the server has not shown back yet, by word.
    const ownWords = new Map<string, string[]>();
    for (const id of [...waitingKnownAdds, ...answeredKnownAdds]) {
      const word = known.get(id);
      if (!word) continue;
      const key = wordKey(word.language, word.lemma);
      ownWords.set(key, [...(ownWords.get(key) ?? []), id]);
    }

    // 3. A full refresh notes every id it receives, over all its answers.
    const started = notes.paging ? notes.refresh : null;
    let refresh = answer.full ? (started ?? { cards: [], known: [], logs: [] }) : null;

    // 4. Rows first...
    for (const row of answer.changes.cards) {
      const card = rowToCard(row);
      refresh?.cards.push(card.id);
      if (untouchable(card.id)) {
        held[card.id] = card;
      } else {
        delete held[card.id];
        cards.put(card);
      }
    }
    for (const row of answer.changes.known_words) {
      const word = rowToKnownWord(row);
      refresh?.known.push(word.id);
      // The learner is removing it: it must not reappear before that is answered.
      if (
        waitingKnownRemovals.has(word.id) ||
        waitingGroupRemovals.has(wordKey(word.language, word.base_lemma ?? word.lemma))
      ) {
        continue;
      }
      // The account has one row per word. A row this device made for the same word, which the
      // server therefore did not take (or will not take), gives way to the server's.
      for (const id of ownWords.get(wordKey(word.language, word.lemma)) ?? []) {
        if (id !== word.id) known.drop(id);
      }
      known.put(word);
    }
    for (const row of answer.changes.review_logs) {
      const log = rowToReviewLog(row);
      refresh?.logs.push(log.id);
      logs.put(log);
    }
    // ...then deletions. A deletion is applied whatever is waiting: operations for a deleted
    // card will be answered "gone".
    for (const deleted of answer.changes.deleted ?? []) {
      if (deleted.kind === 'card') dropCard(deleted.id);
      else known.drop(deleted.id);
    }

    // 5. The last answer of a pull.
    if (!answer.more) {
      if (refresh) {
        // The saved copy is replaced by what arrived; only what still waits to be sent stays.
        const seenCards = new Set(refresh.cards);
        const seenKnown = new Set(refresh.known);
        const seenLogs = new Set(refresh.logs);
        cards.keepOnly((c) => seenCards.has(c.id) || waitingCards.has(c.id));
        known.keepOnly((w) => seenKnown.has(w.id) || waitingKnownAdds.has(w.id));
        logs.keepOnly((l) => seenLogs.has(l.id) || waitingReviews.has(l.id));
        refresh = null;
      }
      settlingCards.clear();
    }

    // 6. Rows kept back for cards nothing waits for any more are the server's last word.
    for (const [cardId, card] of Object.entries(held)) {
      if (untouchable(cardId)) continue;
      delete held[cardId];
      cards.put(card);
    }

    // 7. The account's settings, except those the learner changed here and has not sent yet.
    let settingsChanged: Profile | null = null;
    let onboardingChanged = false;
    const remote = answer.changes.profile;
    let reset = notes.reset;
    if (remote) {
      const resetAt = remote.data_reset_at ?? null;
      // Not while a full refresh is already running: its answers carry the same new value.
      if (reset !== undefined && resetAt !== reset && !answer.full) refreshNeeded = true;
      reset = resetAt;

      const waitingSettings = new Set(
        left.flatMap((op) => (op.type === 'settings.set' ? Object.keys(op) : []))
      );
      const local = await StorageManager.getProfile();
      const patch: Partial<Profile> = {};
      if (
        !waitingSettings.has('native_language') &&
        isTranslationLanguage(remote.native_language) &&
        remote.native_language !== local.native_language
      ) {
        patch.native_language = remote.native_language;
      }
      for (const key of Object.keys(SETTING_RANGES) as (keyof typeof SETTING_RANGES)[]) {
        const value = remote[key];
        const [min, max] = SETTING_RANGES[key];
        // A number the app could not have saved is not taken over.
        if (waitingSettings.has(key) || !Number.isInteger(value) || value < min || value > max) {
          continue;
        }
        if (value !== local[key]) patch[key] = value;
      }
      if (Object.keys(patch).length > 0) settingsChanged = await StorageManager.saveProfile(patch);

      if (!waitingSettings.has('onboarding_completed')) {
        const done = await StorageManager.isOnboardingComplete();
        if (done !== !!remote.onboarding_completed_at) {
          await StorageManager.saveOnboarding(remote.onboarding_completed_at);
          onboardingChanged = true;
        }
      }
    }

    // 8. Write: the saved copy, then the notes with the new cursor, the outbox last (see the
    //    top of this file for why in this order).
    const cacheChanged =
      cards.changed || known.changed || logs.changed || !!settingsChanged || onboardingChanged;
    if (cards.changed) await StorageManager.saveCards(cards.list());
    if (known.changed) await StorageManager.saveKnownWords(known.list());
    if (logs.changed) await StorageManager.saveReviewLogs(logs.list());
    await StorageManager.setLocalValue<SyncNotes>(
      keys.state,
      refreshNeeded
        ? { ...EMPTY_NOTES, held, reset, rev: notes.rev + 1 }
        : {
            cursor: answer.cursor,
            paging: answer.more,
            settlingCards: [...settlingCards],
            settlingKnown: answer.more ? [...answeredKnownAdds] : [],
            held,
            refresh,
            reset,
            rev: notes.rev + (cacheChanged ? 1 : 0),
          }
    );
    if (sent.length > 0) await StorageManager.setLocalValue(keys.outbox, left);

    return {
      current: true,
      again: answer.more || refreshNeeded,
      outboxLeft: left.length,
      settingsChanged,
    };
  });
}

function emit<T>(listeners: Set<(value: T) => void>, value: T): void {
  for (const listener of listeners) {
    try {
      listener(value);
    } catch (err) {
      console.warn('[LingoPing Sync] A listener threw:', err);
    }
  }
}

export function createSyncEngine(options: SyncEngineOptions = {}): SyncEngine {
  const requestTimeoutMs = options.requestTimeoutMs ?? REQUEST_TIMEOUT_MS;
  const transport = options.transport ?? edgeFunctionTransport(requestTimeoutMs);
  const moveOver = options.moveOver ?? ((userId: string) => moveOverToCloudFirst(userId));
  const sendDelayMs = options.sendDelayMs ?? SEND_DELAY_MS;
  const random = options.random ?? Math.random;

  const cacheListeners = new Set<(value: void) => void>();
  const settingsListeners = new Set<(profile: Profile) => void>();
  const sentListeners = new Set<(value: void) => void>();
  const updateListeners = new Set<(value: void) => void>();

  let stopped = false;
  let sendTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Failures in a row; decides how long to wait before the next try. */
  let failures = 0;
  let retryAt = 0;
  /** The round in progress, and whether somebody asked for another one meanwhile. */
  let running: Promise<SyncOutcome> | null = null;
  let askedAgain = false;
  /** The `rev` (of which learner) this page has shown; another one in storage means: redraw. */
  let seen: { userId: string; rev: number } | null = null;

  function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
    if (timer) clearTimeout(timer);
    return null;
  }

  /** A request that gives up after the time limit, whatever the transport does. */
  function request(body: Parameters<SyncTransport>[0]): Promise<SyncAnswer> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new SyncRequestError('retry', 'The server did not answer in time.')),
        requestTimeoutMs
      );
      transport(body).then(
        (answer) => {
          clearTimeout(timer);
          resolve(answer);
        },
        (err) => {
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  /** Tells this page's listeners when the saved copy changed, whichever page wrote it. */
  async function noticeChanges(userId: string): Promise<void> {
    const { rev } = await readNotes(userId);
    const before = seen;
    seen = { userId, rev };
    if (before?.userId === userId && before.rev !== rev) emit(cacheListeners, undefined);
  }

  /** Before the first round: what this page has shown is the saved copy as it is now. */
  async function rememberShown(userId: string): Promise<void> {
    if (seen?.userId !== userId) seen = { userId, rev: (await readNotes(userId)).rev };
  }

  /**
   * One round under the `sync` lock: move an old installation over if needed, then send and
   * receive until the outbox is empty and the server has nothing more.
   */
  async function round(userId: string): Promise<SyncOutcome> {
    let sentSomething = false;
    /** Whether the server says the learner has no access: only reading, no operations. */
    let locked = false;
    /** After the server refused a whole request: operations go one by one, to find the bad one. */
    let perRequest = MAX_OPS_PER_REQUEST;

    const work = StorageManager.locked('sync', async (): Promise<SyncOutcome> => {
      if ((await StorageManager.getCurrentUserId({ fresh: true })) !== userId) return 'off';
      await moveOver(userId);
      const device = await deviceId(userId);
      // What was last said; the pull below finds out whether it still holds.
      locked = isLocked(await getAccess());

      for (;;) {
        const notes = await readNotes(userId);
        // While a pull is being paged, or while access is locked, the request carries no
        // operations (the contract).
        const batch =
          notes.paging || locked
            ? []
            : (await StorageManager.getOutbox(userId)).slice(0, perRequest);

        let answer: SyncAnswer;
        try {
          answer = await request({
            device_id: device,
            ops: batch,
            since: notes.cursor,
            senses: true,
            client: syncClient(),
          });
        } catch (err) {
          if (!(err instanceof SyncRequestError) || err.kind !== 'refused') throw err;
          // 400: nothing was applied. One operation in the request cannot even be read.
          if (batch.length > 1) {
            perRequest = 1;
            continue;
          }
          console.warn('[LingoPing Sync] The server could not read a request.', batch[0]);
          if (batch.length === 0 && notes.cursor === null) throw err;
          await StorageManager.locked('data', async () => {
            const keys = syncKeys(userId);
            // The saved copy may hold what that operation did: fetch everything again.
            await StorageManager.setLocalValue(keys.state, { ...EMPTY_NOTES, rev: notes.rev });
            if (batch.length === 1) {
              const outbox = await StorageManager.getOutbox(userId);
              await StorageManager.setLocalValue(
                keys.outbox,
                outbox.filter((op) => op.id !== batch[0].id)
              );
            }
          });
          continue;
        }

        // The server works with this version (again), and says which is the oldest it accepts.
        await clearUpdateRequired();
        await rememberMinExtensionVersion(answer.min_extension_version ?? null);
        const applied = await applyAnswer(userId, batch, answer);
        if (!applied.current) return 'off';
        const refused = answer.results.filter((r) => !r.ok && r.error === 'locked').length;
        if (batch.length > refused) sentSomething = true;
        // Access first, then what is waiting: a lock found now stops the sending, and access
        // found back sends the outbox in this round.
        const access = await saveAccess(userId, answer.access);
        locked = access ? isLocked(access) : refused > 0;
        if (applied.settingsChanged) emit(settingsListeners, applied.settingsChanged);
        if (applied.again || (!locked && applied.outboxLeft > 0)) continue;
        return 'synced';
      }
    });

    try {
      return await work;
    } finally {
      // Also after a round that failed half-way: what it did write is there to be shown.
      if (sentSomething) emit(sentListeners, undefined);
      await noticeChanges(userId).catch(() => undefined);
    }
  }

  async function run(): Promise<SyncOutcome> {
    const userId = await StorageManager.getCurrentUserId({ fresh: true });
    if (!userId || isDevMockUserId(userId)) return 'off';
    try {
      await rememberShown(userId);
      const outcome = await round(userId);
      failures = 0;
      retryAt = 0;
      retryTimer = clearTimer(retryTimer);
      return outcome;
    } catch (err) {
      if (err instanceof SyncRequestError && err.kind === 'signed_out') return 'off';
      if (err instanceof SyncRequestError && err.kind === 'update_required') {
        // Too old for the server: nothing is sent until the extension is updated (it asks Chrome
        // for the update and says so); trying again now and then finds out when it is.
        await setUpdateRequired(err.minVersion ?? null);
        emit(updateListeners, undefined);
        failures = Math.max(failures, 20);
      }
      failures++;
      // 2 s, 4 s, 8 s ... up to five minutes, spread a little so devices do not retry in step.
      const delay =
        Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * 2 ** (failures - 1)) * (0.8 + 0.4 * random());
      retryAt = Date.now() + delay;
      retryTimer = clearTimer(retryTimer);
      if (!stopped) retryTimer = setTimeout(() => void syncNow(), delay);
      console.warn(
        `[LingoPing Sync] Not synced (${(err as Error)?.message ?? err}); trying again in ${Math.round(delay / 1000)} s.`
      );
      return 'failed';
    }
  }

  /** One round at a time per page; a call during a round makes it go once more afterwards. */
  function start(): Promise<SyncOutcome> {
    if (running) {
      askedAgain = true;
      return running;
    }
    running = (async () => {
      let outcome: SyncOutcome;
      do {
        askedAgain = false;
        outcome = await run();
      } while (askedAgain && outcome === 'synced' && !stopped);
      return outcome;
    })().finally(() => {
      running = null;
    });
    return running;
  }

  function syncNow(opts: { timeoutMs?: number } = {}): Promise<SyncOutcome> {
    sendTimer = clearTimer(sendTimer);
    const done = start().catch((err): SyncOutcome => {
      console.error('[LingoPing Sync] Unexpected error:', err);
      return 'failed';
    });
    if (opts.timeoutMs === undefined) return done;
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve('timeout'), opts.timeoutMs);
      void done.then((outcome) => {
        clearTimeout(timer);
        resolve(outcome);
      });
    });
  }

  /** About a second after the last change, so a burst of changes travels together. */
  function sendSoon(): void {
    if (stopped) return;
    sendTimer = clearTimer(sendTimer);
    sendTimer = setTimeout(() => void syncNow(), sendDelayMs);
  }
  const unsubscribe = StorageManager.onOutboxChange(sendSoon);

  return {
    enqueue: (op) => StorageManager.enqueue(op),
    syncNow,
    async tick() {
      if (Date.now() < retryAt) {
        const userId = await StorageManager.getCurrentUserId();
        if (userId) await noticeChanges(userId);
        return 'failed';
      }
      return syncNow();
    },
    async pendingCount() {
      const userId = await StorageManager.getCurrentUserId({ fresh: true });
      if (!userId || isDevMockUserId(userId)) return 0;
      return (await StorageManager.getOutbox(userId)).length;
    },
    retryDelayMs: () => Math.max(0, retryAt - Date.now()),
    onCacheChanged(listener) {
      cacheListeners.add(listener);
      return () => cacheListeners.delete(listener);
    },
    onSettingsChanged(listener) {
      settingsListeners.add(listener);
      return () => settingsListeners.delete(listener);
    },
    onUpdateRequired(listener) {
      updateListeners.add(listener);
      return () => updateListeners.delete(listener);
    },
    onSent(listener) {
      sentListeners.add(listener);
      return () => sentListeners.delete(listener);
    },
    stop() {
      stopped = true;
      sendTimer = clearTimer(sendTimer);
      retryTimer = clearTimer(retryTimer);
      unsubscribe();
    },
  };
}

let shared: SyncEngine | null = null;

/** The engine of this page (or of the background worker), made on first use. */
export function getSyncEngine(): SyncEngine {
  shared ??= createSyncEngine();
  return shared;
}

/** How many pages' worth of listeners are attached (React mounts effects twice in development). */
let pageUsers = 0;
let stopPage: (() => void) | null = null;

/**
 * Keeps a page in step with the account while it is open: it syncs now, when the window gets
 * the focus back, when the connection returns, and every 60 seconds. When the page is hidden or
 * closed it sends what is waiting at once and asks the background worker to do the same, in case
 * the page is gone before the answer arrives. Settings that arrive changed are passed on to the
 * worker, which reschedules its reminder.
 *
 * Returns the function that undoes it. For the dashboard, the popup, the review window and the
 * website (where there is no worker and the notices go nowhere).
 */
export function startPageSync(): () => void {
  pageUsers++;
  if (!stopPage) {
    const engine = getSyncEngine();
    const now = () => void engine.syncNow();
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return now();
      void engine.pendingCount().then((pending) => {
        if (pending === 0) return;
        now();
        platform.notifyBackground({ type: 'SYNC_NOW' });
      });
    };
    const offSettings = engine.onSettingsChanged(() =>
      platform.notifyBackground({ type: 'UPDATE_ALARM' })
    );
    const offCache = engine.onCacheChanged(() =>
      platform.notifyBackground({ type: 'REFRESH_BADGE' })
    );
    window.addEventListener('focus', now);
    window.addEventListener('online', now);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(() => void engine.tick(), PAGE_PULL_INTERVAL_MS);
    now();
    stopPage = () => {
      window.removeEventListener('focus', now);
      window.removeEventListener('online', now);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
      offSettings();
      offCache();
    };
  }
  return () => {
    pageUsers--;
    if (pageUsers > 0 || !stopPage) return;
    stopPage();
    stopPage = null;
  };
}
